import React from 'react';
import { X, Download, AlertCircle, CheckCircle } from 'lucide-react';

const DownloadProgressToast = ({
  filename,
  progress,
  bytesDownloaded,
  totalBytes,
  processedRows,
  totalRows,
  status,
  error,
  onCancel,
  onClose
}) => {
  // Format bytes to human readable
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Format number with commas
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Calculate download speed and ETA
  const getProgressText = () => {
    if (status === 'completed') {
      if (totalRows > 0) {
        return `Exported ${formatNumber(totalRows)} rows`;
      }
      return `Download complete`;
    }
    if (status === 'error') {
      return error || 'Export failed';
    }
    if (totalRows > 0 && processedRows > 0) {
      return `Processing ${formatNumber(processedRows)} / ${formatNumber(totalRows)} rows (${Math.round(progress)}%)`;
    }
    if (totalRows > 0) {
      return `Preparing to export ${formatNumber(totalRows)} rows...`;
    }
    return `Preparing export...`;
  };

  const getStatusIcon = () => {
    if (status === 'completed') {
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    }
    if (status === 'error') {
      return <AlertCircle className="h-5 w-5 text-red-500" />;
    }
    return <Download className="h-5 w-5 text-blue-500" />;
  };

  const getStatusColor = () => {
    if (status === 'completed') return 'border-green-500';
    if (status === 'error') return 'border-red-500';
    return 'border-blue-500';
  };

  return (
    <div
      className={`fixed bottom-6 right-6 bg-slate-800 border-2 ${getStatusColor()} rounded-lg shadow-2xl p-4 w-96 z-[200] animate-slide-up`}
      style={{
        animation: 'slideUp 0.3s ease-out'
      }}
    >
      <style>
        {`
          @keyframes slideUp {
            from {
              transform: translateY(100%);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
        `}
      </style>

      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          {getStatusIcon()}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-200 truncate">
              {status === 'completed' ? 'Download Complete' :
               status === 'error' ? 'Download Failed' :
               'Downloading...'}
            </p>
            <p className="text-xs text-slate-400 truncate" title={filename}>
              {filename}
            </p>
          </div>
        </div>
        <button
          onClick={status === 'completed' || status === 'error' ? onClose : onCancel}
          className="text-slate-400 hover:text-slate-200 ml-2"
          title={status === 'downloading' ? 'Cancel' : 'Close'}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Progress Bar */}
      {status === 'downloading' && (
        <div className="mb-3">
          <div className="w-full bg-slate-700 rounded-full h-2 overflow-hidden">
            <div
              className="bg-blue-500 h-full transition-all duration-300 ease-out"
              style={{
                width: `${totalRows > 0 && progress > 0 ? progress : 100}%`,
                transition: 'width 0.3s ease-out'
              }}
            >
              {(totalRows === 0 || progress === 0) && (
                <div
                  className="h-full w-full bg-blue-400 animate-pulse"
                  style={{
                    animation: 'pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Progress Text */}
      <p className="text-xs text-slate-300 mb-2">
        {getProgressText()}
      </p>

      {/* Action Buttons */}
      {status === 'downloading' && (
        <button
          onClick={onCancel}
          className="w-full bg-red-600 text-white text-sm py-1.5 rounded hover:bg-red-700 transition-colors"
        >
          Cancel Download
        </button>
      )}

      {status === 'completed' && (
        <button
          onClick={onClose}
          className="w-full bg-green-600 text-white text-sm py-1.5 rounded hover:bg-green-700 transition-colors"
        >
          Close
        </button>
      )}

      {status === 'error' && (
        <div className="space-y-2">
          <p className="text-xs text-red-400">{error}</p>
          <button
            onClick={onClose}
            className="w-full bg-slate-600 text-white text-sm py-1.5 rounded hover:bg-slate-500 transition-colors"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
};

export default DownloadProgressToast;
